import { atomStore } from '@figma/jotai'

import { initialOptions } from 'lib/initial_options'
import { defaultConfig } from 'lib/statsig/internals/experiment'

import {
  type DynamicConfig,
  EvaluationReason,
  makeDynamicConfig,
} from './dynamic_config_primitives'
import { statsigClientAtom } from './statsig_atoms'
import { statsigStatusAtom, statsigSuspendedAtom } from './statsig_state'
import { StatsigInitStatus } from './types'
import { getDCOverridesFromLocalStorage } from './utils'

/** Applies figma_dc_overrides localStorage overrides to a config at read time (for E2E tests only) */
export function applyDCOverride(configName: string, config: DynamicConfig): DynamicConfig {
  if (config === defaultConfig) return config
  const override = getDCOverridesFromLocalStorage()[configName]
  if (override == null) return config
  return makeDynamicConfig(
    configName,
    config.details,
    { ...config.value, ...override },
    config.ruleID,
  )
}

export async function getDynamicConfig(
  configName: string,
  disableExposureLogging?: boolean,
): Promise<DynamicConfig> {
  // Suspend the rest of the code until the upgraded SDK is loaded — that's the
  // client getDynamicConfig reads from.
  await atomStore.get(statsigSuspendedAtom)

  // The await settling does NOT mean init completed, so re-read the status and serve
  // defaults unless the upgraded SDK is actually ready — three not-COMPLETED cases:
  // init errored, a re-init in flight on a context switch, or the SDK not loaded.
  const statsigStatus = atomStore.get(statsigStatusAtom)
  if (statsigStatus.status !== StatsigInitStatus.COMPLETED) {
    return defaultConfig
  }

  try {
    const statsigClient = atomStore.get(statsigClientAtom)
    if (!statsigClient) {
      // This will happen if we try and fetch a config before Statsig is initialized
      return defaultConfig
    }

    const returnedConfig = disableExposureLogging
      ? statsigClient.getDynamicConfig(configName, {
          disableExposureLog: true,
        })
      : statsigClient.getDynamicConfig(configName)

    const result = makeDynamicConfig(
      configName,
      {
        reason: returnedConfig.details.reason,
        warnings: returnedConfig.details.warnings,
      },
      returnedConfig.value,
      returnedConfig.ruleID,
    )

    return applyDCOverride(configName, result)
  } catch (err) {
    return defaultConfig
  }
}

export function getDynamicConfigFromInitialOptions(configName: string): DynamicConfig {
  const configValue = initialOptions()?.dynamic_configs?.[configName]
  if (configValue) {
    return makeDynamicConfig(configName, { reason: EvaluationReason.Unrecognized }, configValue, '')
  }

  return defaultConfig
}
